<template>
    <div class="row g-3 mb-4">
        <!-- NAGŁÓWEK SEKCJI -->
        <div ref="headerRef" class="col-12 d-flex justify-content-between align-items-center border-bottom pb-2 cursor-pointer" @click="toggleSection">
            <h6 class="fw-bold mb-0">2a. Usługi dodatkowe (Gwarancja i OC)</h6>
            <button type="button" class="btn btn-sm btn-link text-decoration-none p-0">
                <i class="bi" :class="sectionCollapse ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </button>
        </div>

        <!-- TREŚĆ SEKCJI -->
        <div class="col-12" v-show="sectionCollapse">
            <!-- DANE KLIENTA DO GWARANCJI -->
            <div class="row g-3 mb-3 p-3 bg-light rounded border">
                <div class="col-md-4">
                    <label class="form-label fw-semibold small mb-1">PESEL klienta</label>
                    <input type="text" class="form-control form-control-sm" v-model="clientData.pesel" placeholder="np. 85010112345" maxlength="11" />
                </div>
                <div class="col-md-4">
                    <label class="form-label fw-semibold small mb-1">Numer karty klienta</label>
                    <input type="text" class="form-control form-control-sm" v-model="clientData.kartaKlienta" placeholder="np. KK-123456" />
                </div>
                <div class="col-md-4 d-flex align-items-end">
                    <button 
                        type="button" 
                        class="btn btn-sm btn-primary w-100 d-flex align-items-center justify-content-center gap-1"
                        :disabled="loading || !vehicle?.vin"
                        @click="fetchCalculations"
                    >
                        <i class="bi" :class="loading ? 'bi-arrow-repeat spin' : 'bi-calculator'"></i>
                        <span>{{ hasCalculations ? 'Przelicz' : 'Oblicz ofertę' }}</span>
                    </button>
                </div>
            </div>

            <!-- CZERWONY ALERT W PRZYPADKU BŁĘDU -->
            <div v-if="errorMessage" class="alert alert-danger d-flex align-items-center gap-2 mb-3 py-2 px-3 small" role="alert">
                <i class="bi bi-exclamation-triangle-fill fs-5 flex-shrink-0"></i>
                <div>{{ errorMessage }}</div>
            </div>

            <!-- STAN ŁADOWANIA -->
            <div v-if="loading" class="d-flex flex-column gap-2">
                <div v-for="n in 3" :key="n" class="card border p-3 shadow-sm bg-light skeleton-card">
                    <div class="d-flex justify-content-between align-items-center">
                        <div class="skeleton-line title"></div>
                        <div class="skeleton-line price"></div>
                    </div>
                </div>
            </div>

            <!-- BRAK WYLICZEŃ -->
            <div v-else-if="!hasCalculations" class="text-center py-3 text-muted bg-light border rounded">
                <i class="bi bi-shield-check fs-2 d-block mb-1 text-secondary"></i>
                <span class="small">Uzupełnij VIN pojazdu oraz opcjonalnie PESEL/Kartę klienta, a następnie kliknij <strong>"Oblicz ofertę"</strong>.</span>
            </div>

            <!-- LISTA USŁUG JEDNA POD DRUGĄ -->
            <div v-else class="d-flex flex-column gap-2">
                
                <!-- SEKCJA GWARANCJI -->
                <div v-if="pricesList.length > 0">
                    <div class="fw-bold small text-secondary mb-2">Pakiety Gwarancji (wybierz maksymalnie jeden):</div>
                    <div class="d-flex flex-column gap-2 mb-3">
                        <div 
                            v-for="(priceItem, idx) in pricesList" 
                            :key="idx"
                            class="card border p-3 cursor-pointer transition-all d-flex flex-row align-items-center justify-content-between"
                            :class="{ 'border-primary bg-primary-subtle shadow-sm': selectedWarranty?.ID === priceItem.ID }"
                            @click="selectWarranty(priceItem)"
                        >
                            <div class="d-flex align-items-center gap-3">
                                <input 
                                    type="radio" 
                                    name="warrantyOption" 
                                    class="form-check-input fs-5 m-0 cursor-pointer" 
                                    :checked="selectedWarranty?.ID === priceItem.ID"
                                />
                                <div>
                                    <div class="fw-bold text-dark text-capitalize">
                                        Gwarancja CarGwarant {{ priceItem.packetName || priceItem.packet }}
                                    </div>
                                    <div class="small text-muted">
                                        Okres ochrony: <span class="badge bg-secondary-subtle text-dark">{{ priceItem.okresMiesiecy }} mies.</span>
                                    </div>
                                </div>
                            </div>
                            <div class="fs-5 fw-semibold text-primary">
                                {{ formatPrice(priceItem.cena) }} <span class="fs-6 fw-normal text-muted">brutto</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- SEKCJA OC -->
                <div v-if="ocPrice !== null">
                    <div class="fw-bold small text-secondary mb-2">Opcje dodatkowe (możesz zaznaczyć niezależnie):</div>
                    <div 
                        class="card border p-3 cursor-pointer transition-all d-flex flex-row align-items-center justify-content-between"
                        :class="{ 'border-success bg-success-subtle shadow-sm': isOcSelected }"
                        @click="toggleOc"
                    >
                        <div class="d-flex align-items-center gap-3">
                            <input 
                                type="checkbox" 
                                class="form-check-input fs-5 m-0 cursor-pointer" 
                                :checked="isOcSelected"
                            />
                            <div>
                                <div class="fw-bold text-dark">Ubezpieczenie OC / Assistance</div>
                                <div class="small text-muted">Dodatkowa ochrona ubezpieczeniowa pojazdu</div>
                            </div>
                        </div>
                        <div class="fs-5 fw-semibold text-success">
                            {{ formatPrice(ocPrice) }} <span class="fs-6 fw-normal text-muted">brutto</span>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import apiClient from '@/api'

const props = defineProps({
    vehicle: { type: Object, default: () => ({}) },
    amount: { type: [Number, String], default: 0 },
    selectedAddons: {
        type: Object,
        default: () => ({ warranty: null, oc: null })
    }
})

const emit = defineEmits(['update:selectedAddons', 'updateItems'])

const sectionCollapse = ref(false)
const headerRef = ref(null)
const loading = ref(false)
const errorMessage = ref('')

const clientData = ref({ pesel: '', kartaKlienta: '' })
const pricesList = ref([])
const ocPrice = ref(null)

const hasCalculations = computed(() => pricesList.value.length > 0 || ocPrice.value !== null)

const selectedWarranty = computed(() => props.selectedAddons?.warranty || null)
const isOcSelected = computed(() => !!props.selectedAddons?.oc)

const toggleSection = async () => {
    sectionCollapse.value = !sectionCollapse.value
    if (sectionCollapse.value) {
        await nextTick()
        headerRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
}

const fetchCalculations = async () => {
    if (!props.vehicle?.vin) return

    loading.value = true
    errorMessage.value = ''

    try {
        const response = await apiClient.post(
            {
                action: 'calculate-addons',
                vehicle: props.vehicle,
                client: clientData.value,
                amount: props.amount
            }
        )

        if (response.data?.status === 'error') {
            errorMessage.value = response.data.message || 'Wystąpił błąd podczas pobierania oferty.'
            pricesList.value = []
            ocPrice.value = null
        } else {
            if (response.data?.prices) {
                pricesList.value = Array.isArray(response.data.prices) ? response.data.prices : Object.values(response.data.prices)
            }
            if (response.data?.oc !== undefined) {
                ocPrice.value = response.data.oc
            }
        }
    } catch (err) {
        errorMessage.value = 'Nie udało się połączyć z serwerem.'
        console.error('Błąd podczas pobierania oferty:', err)
    } finally {
        loading.value = false
    }
}

// Wybór / odznaczenie gwarancji
const selectWarranty = (item) => {
    const newWarranty = selectedWarranty.value?.ID === item.ID ? null : item
    const updated = { ...props.selectedAddons, warranty: newWarranty }
    
    emit('update:selectedAddons', updated)
    emit('updateItems', updated)
}

// Wybór / odznaczenie OC
const toggleOc = () => {
    const newOc = isOcSelected.value ? null : { cena: ocPrice.value }
    const updated = { ...props.selectedAddons, oc: newOc }

    emit('update:selectedAddons', updated)
    emit('updateItems', updated)
}

const formatPrice = (val) => {
    if (!val) return '0,00 PLN'
    return Number(val).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' PLN'
}
</script>

<style scoped>
.cursor-pointer { cursor: pointer; user-select: none; }
.transition-all { transition: all 0.15s ease-in-out; }
.spin { animation: spin 1s linear infinite; }
@keyframes spin { 100% { transform: rotate(360deg); } }

.skeleton-card { height: 60px; }
.skeleton-line {
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 4px;
}
.skeleton-line.title { height: 18px; width: 40%; }
.skeleton-line.price { height: 22px; width: 20%; }

@keyframes loading {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}
</style>